import {
  Badge,
  Button,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Spinner,
  Stat,
  StatGroup,
  StatLabel,
  StatNumber,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
} from '@chakra-ui/react';
import { FC, useState } from 'react';
import { preVisualizarVitrine } from '../api';
import { ContextoVitrine, ParametrosPreview, RespostaPreview } from '../types';
import {
  formatarDecimal,
  formatarInteiro,
  formatarPercentual,
  lerProductIds,
} from '../utils';
import Secao from './Secao';

interface Props {
  contexto: ContextoVitrine;
  /** Valida e devolve os parâmetros atuais do formulário (null se inválidos). */
  obterParametros: () => Promise<ParametrosPreview | null>;
}

const mensagemDeErro = (erro: any): string =>
  erro?.code === 'ECONNABORTED'
    ? 'A consulta demorou mais que o esperado. Tente novamente em instantes.'
    : erro?.response?.data?.message ||
      erro?.message ||
      'Não foi possível pré-visualizar, tente novamente mais tarde';

const PreviewPanel: FC<Props> = ({ contexto, obterParametros }) => {
  const toast = useToast();
  const [texto, setTexto] = useState('');
  const [carregando, setCarregando] = useState(false);
  const [resultado, setResultado] = useState<RespostaPreview | null>(null);
  const ehSacola = contexto === 'sacola';

  const avisarErro = (descricao: string) =>
    toast({
      title: 'Opss!!',
      description: descricao,
      status: 'error',
      position: 'top-right',
    });

  const preVisualizar = async () => {
    if (carregando) return;
    const productIds = lerProductIds(texto);
    if (!productIds.length) {
      avisarErro('Informe o id de pelo menos um produto da VTEX');
      return;
    }
    if (!ehSacola && productIds.length > 1) {
      avisarErro('Na PDP informe só um produto');
      return;
    }
    if (productIds.some(id => !/^\d{1,9}$/.test(id))) {
      avisarErro('Informe ids numéricos da VTEX, com até 9 dígitos');
      return;
    }
    if (productIds.length > 10) {
      avisarErro('Use no máximo 10 produtos por prévia da sacola');
      return;
    }

    const settings = await obterParametros();
    if (!settings) {
      avisarErro('Corrija os campos do algoritmo antes de pré-visualizar');
      return;
    }

    setCarregando(true);
    try {
      setResultado(
        await preVisualizarVitrine({ context: contexto, productIds, settings }),
      );
    } catch (erro) {
      setResultado(null);
      avisarErro(mensagemDeErro(erro));
    } finally {
      setCarregando(false);
    }
  };

  return (
    <Secao
      titulo="Pré-visualizar"
      descricao="Teste os parâmetros do algoritmo acima antes de salvar. Nada é alterado no app."
    >
      <Flex gap={4} align="flex-end" flexWrap="wrap" mb={6}>
        <FormControl maxW="420px">
          <FormLabel htmlFor={`preview-${contexto}`}>
            {ehSacola ? 'Produtos na sacola (VTEX)' : 'Produto (VTEX)'}
          </FormLabel>
          <Input
            id={`preview-${contexto}`}
            bg="white"
            focusBorderColor="brand.500"
            placeholder={ehSacola ? 'Ex.: 21262, 24415' : 'Ex.: 21262'}
            value={texto}
            onChange={evento => setTexto(evento.target.value)}
            onKeyDown={evento => {
              if (evento.key === 'Enter') {
                evento.preventDefault();
                preVisualizar();
              }
            }}
          />
          <FormHelperText>
            {ehSacola
              ? 'Até 10 ids de produto da VTEX separados por vírgula'
              : 'Id de produto da VTEX'}
          </FormHelperText>
        </FormControl>
        <Button
          colorScheme="brand"
          variant="outline"
          isLoading={carregando}
          onClick={preVisualizar}
          mb={7}
        >
          Pré-visualizar com estes parâmetros
        </Button>
      </Flex>

      {carregando && !resultado && (
        <Flex justify="center" py={6}>
          <Spinner color="brand.500" />
        </Flex>
      )}

      {resultado && (
        <>
          <StatGroup mb={4} maxW="520px">
            <Stat>
              <StatLabel>Pedidos analisados</StatLabel>
              <StatNumber fontSize="xl">
                {formatarInteiro(resultado.meta.totalOrders)}
              </StatNumber>
            </Stat>
            <Stat>
              <StatLabel>
                {ehSacola ? 'Pedidos com os produtos' : 'Pedidos com o produto'}
              </StatLabel>
              <StatNumber fontSize="xl">
                {formatarInteiro(resultado.meta.targetOrders)}
              </StatNumber>
            </Stat>
          </StatGroup>
          <Flex gap={2} mb={4}>
            {!!resultado.meta.network && (
              <Badge colorScheme="blue">Rede {resultado.meta.network}</Badge>
            )}
            {resultado.meta.fallback && (
              <Badge colorScheme="orange">Resultado de contingência</Badge>
            )}
          </Flex>

          {!resultado.items.length ? (
            <Text my={6} color="gray.600">
              Sem recomendações para esses produtos com os parâmetros atuais
            </Text>
          ) : (
            <TableContainer
              border="1px solid #D6E8FF"
              borderRadius="12px"
              opacity={carregando ? 0.5 : 1}
            >
              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th isNumeric>Posição</Th>
                    <Th>Produto</Th>
                    <Th>Categoria</Th>
                    {ehSacola && <Th>Origem</Th>}
                    <Th isNumeric>Pedidos juntos</Th>
                    <Th isNumeric>Confiança</Th>
                    <Th isNumeric>Lift</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {resultado.items.map(item => (
                    <Tr key={`${item.rank}-${item.productId}`}>
                      <Td isNumeric>{item.rank}</Td>
                      <Td whiteSpace="normal" minW="240px">
                        <Text fontWeight="medium">{item.name}</Text>
                        <Text fontSize="xs" color="gray.500">
                          Id {item.productId}
                        </Text>
                      </Td>
                      <Td whiteSpace="normal">{item.category || '-'}</Td>
                      {ehSacola && <Td>{item.sourceProductId || '-'}</Td>}
                      <Td isNumeric>{formatarInteiro(item.ordersTogether)}</Td>
                      <Td isNumeric>{formatarPercentual(item.confidence)}</Td>
                      <Td isNumeric>{formatarDecimal(item.lift)}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          )}
        </>
      )}
    </Secao>
  );
};

export default PreviewPanel;
